import History from '@history/@history';
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit';
import { ObjectId } from 'app/common.types';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import { crmFieldsSelector } from 'app/main/apps/crm/ducks/crm.selector';
import { actions as crmActions } from 'app/main/apps/crm/ducks/crm.slice';
import { saveCrmFieldsThunk } from 'app/main/apps/crm/ducks/crm.thunk';
import { ClientCrmField, CrmFieldStatusEnum, CrmFieldTypeEnum } from 'app/main/apps/crm/types';
import { selectUserFranchise } from 'app/main/apps/franchises/ducks/franchises.selector';
import { redeemOfferErrorKey } from 'app/main/apps/loyalties/redeemOfferError';
import { LoyaltyCardInDTO } from 'app/main/apps/loyalties/type';
import { currentStoreIdSelector } from 'app/main/apps/stores/ducks/stores.selector';
import { StoreDTO, StoreSaleDTO } from 'app/main/apps/stores/type';
import { CouponStateEnum } from 'app/main/design-system/models/coupon.type';
import { apiErrorStatus } from 'app/services/api/apiErrorStatus';
import jwtService from 'app/services/jwtService';
import { AppDispatch, RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import moment from 'moment';

import {
  addClientToStoreApi,
  addSaleToClientApi,
  deleteClientApi,
  getClientFormResponsesApi,
  getClientLoyaltyApi,
  getClientTopProductsApi,
  getColumnPreferencesApi,
  getFormQuestionColumnsApi,
  getUserClientDetailApi,
  getUserClientsApi,
  getUserClientTransactionsApi,
  getUserCouponsApi,
  redeemCouponApi,
  redeemOfferApi,
  saveColumnPreferencesApi,
  updateCreditPointsClientApi,
} from '../api';
import { keepUnlistedPreferences } from '../columns/keepUnlistedPreferences';
import { draftFromPeriod, periodFromDraft } from '../components/activityPeriodDraft';
import {
  AcquiredFromTypeEnum,
  ActivityPeriod,
  activityPeriodParams,
  ClientCreditPoints,
  ClientDetails,
  ClientRecord,
  ClientsFiltersUrlEnum,
  ClientsSortKey,
  ClientsSortOrderEnum,
  ClientsSortsUrlEnum,
  ClientsSortTypeEnum,
  ClientType,
  ColumnPreference,
  CrmFilter,
  CrmFilterValue,
  crmFilterParams,
  isActivityPeriodUnit,
  isClientRecord,
} from '../type';
import {
  redeemCouponDialogSelector,
  selectClientsFilters,
  selectClientsPagination,
  selectClientsSort,
  selectColumnPreferences,
  selectSelectedClient,
} from './client.selector';
import {
  actions,
  CLIENTS_SORT_DEFAULT,
  CLIENTS_SORT_ORDER_DEFAULT,
  ClientState,
  PAGE,
  ROWS_PER_PAGE,
  setAddColumnDrawerOpened,
  setClientActiveCouponsLoading,
  setClientCouponsFailed,
  setClientCouponsLoading,
  setClientCouponsSuccess,
  setClientFormResponsesFailed,
  setClientFormResponsesLoading,
  setClientFormResponsesSuccess,
  setClientInformationSuccess,
  setClientSelectedCouponsSuccess,
  setClientsFilters,
  setClientsFiltersSortPagination,
  setClientsPagination,
  setClientsResetFiltersSortPagination,
  setClientsSort,
  setClientTopProductsFailed,
  setClientTopProductsLoading,
  setClientTopProductsSuccess,
  setColumnPreferences,
  setColumnsDialogOpened,
  setCreditPointsLoading,
  setFormQuestionColumnsFailed,
  setFormQuestionColumnsLoading,
  setFormQuestionColumnsSuccess,
  setLoading,
  setRedeemCouponDialogLoading,
  setRedeemOfferDialogLoading,
} from './clients.slice';

export const getClientsThunk = createAsyncThunk(
  'clients/getUserClients',
  async (storeIds: EntityId[] | undefined, { getState }) => {
    const { type, order } = selectClientsSort(getState() as RootState);
    const { searchText, acquiredFrom, clientsType, segmentRefs, crmFilter, record, activityPeriod } =
      selectClientsFilters(getState() as RootState);
    const { page, rowsPerPage } = selectClientsPagination(getState() as RootState);
    const clients = await getUserClientsApi(
      type,
      order,
      page + 1,
      rowsPerPage,
      searchText,
      storeIds,
      acquiredFrom,
      clientsType,
      segmentRefs,
      crmFilter,
      record,
      activityPeriod,
    );
    return clients;
  },
);

export const getClientTransactionsThunk = createAsyncThunk(
  'clients/getUserClientTransactions',
  async (
    {
      userId,
      franchiseId,
      storeId,
      page,
      size,
    }: { userId: ObjectId; franchiseId: ObjectId; storeId: ObjectId; page?: number; size?: number },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await getUserClientTransactionsApi(userId, franchiseId, storeId, page, size);
    } catch (error) {
      dispatch(showMessage({ message: 'transactions.errorFetching', variant: 'error', ns: 'client' }));
      return rejectWithValue(error);
    }
  },
);

// The API answers 404 when the user has no loyalty card in the franchise yet.
const CLIENT_NOT_FOUND_ERROR = 'CLIENT_NOT_FOUND';

export const getClientInformation =
  (franchiseId: EntityId, clientId: ClientDetails['id'], storeId?: EntityId) => async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(actions.setClientNotFound(null));
    try {
      const client = await getUserClientDetailApi(franchiseId, clientId, storeId);
      dispatch(actions.setClientInformationSuccess({ ...client }));
      return client;
    } catch (error) {
      dispatch(actions.setClientInformationFailed());
      if (apiErrorStatus(error) === CLIENT_NOT_FOUND_ERROR) dispatch(actions.setClientNotFound(String(clientId)));
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  };

const refreshSelectedClient = async (dispatch: AppDispatch, getState: () => RootState, clientId: EntityId) => {
  const franchise = selectUserFranchise(getState());
  if (!franchise.id) return null;
  dispatch(setLoading(true));
  try {
    const client = await getUserClientDetailApi(franchise.id, String(clientId), currentStoreIdSelector(getState()));
    dispatch(setClientInformationSuccess(client));
    return client;
  } catch (_e) {
    return null;
  } finally {
    dispatch(setLoading(false));
  }
};

export const getClientLoyaltyThunk =
  (franchiseId: EntityId, clientId: ClientDetails['id'], storeId?: string) => async (dispatch: AppDispatch) => {
    try {
      dispatch(actions.setClientLoyaltyLoading(true));
      const { offers } = await getClientLoyaltyApi(franchiseId, clientId, storeId);
      dispatch(actions.setClientLoyaltyOffersSuccess(offers));
      return offers;
    } catch (_e) {
      dispatch(actions.setClientInformationFailed());
      return null;
    } finally {
      dispatch(actions.setClientLoyaltyLoading(false));
    }
  };
export const getClientCouponsThunk =
  (franchiseId: EntityId, clientId: EntityId, storeId?: EntityId) => async (dispatch: AppDispatch) => {
    try {
      dispatch(setClientActiveCouponsLoading(true));
      const { data } = await getUserCouponsApi(
        clientId,
        undefined,
        undefined,
        franchiseId,
        CouponStateEnum.ACTIVE,
        storeId,
      );
      dispatch(actions.setClientSelectedCouponsSuccess(data));
      return data;
    } catch (_e) {
      dispatch(actions.setClientInformationFailed());
      return null;
    } finally {
      dispatch(setClientActiveCouponsLoading(false));
    }
  };

export const getClientTopProductsThunk =
  (franchiseId: EntityId, clientId: ClientDetails['id'], storeId?: string, limit?: number) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setClientTopProductsLoading(true));
      const topProducts = await getClientTopProductsApi(franchiseId, clientId, storeId, limit);
      dispatch(setClientTopProductsSuccess(topProducts));
      return topProducts;
    } catch (_e) {
      dispatch(setClientTopProductsFailed());
      return null;
    }
  };

export const getFormQuestionColumnsThunk = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(setFormQuestionColumnsLoading(true));
    const columns = await getFormQuestionColumnsApi();
    dispatch(setFormQuestionColumnsSuccess(columns));
    return columns;
  } catch (_e) {
    dispatch(setFormQuestionColumnsFailed());
    return null;
  }
};

export const getClientFormResponsesThunk =
  (franchiseId: EntityId, clientId: ClientDetails['id'], page?: number, size?: number) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setClientFormResponsesLoading(true));
      const responses = await getClientFormResponsesApi(franchiseId, clientId, page, size);
      dispatch(
        setClientFormResponsesSuccess({
          data: responses.data,
          total: responses.metadata.total,
          clientId: String(clientId),
        }),
      );
      return responses.data;
    } catch (_e) {
      dispatch(setClientFormResponsesFailed());
      return null;
    }
  };

export const setCreditPointsClient =
  (client: ClientDetails, point: number, storeId: StoreDTO['id']) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const merchantId = jwtService.getUserId();
      if (!merchantId) {
        throw new Error('No user id');
      }
      const clientAddPointEntity: ClientCreditPoints = {
        userId: client.id,
        merchantId,
        storeId,
        points: point,
      };
      dispatch(setCreditPointsLoading(true));
      await updateCreditPointsClientApi(clientAddPointEntity);
      dispatch(actions.toggleCreditPointsModal(false));
      await refreshSelectedClient(dispatch, getState, client.id);
      if (point > 0) {
        dispatch(showMessage({ message: 'loyalty.editPoints.successAddMessage', variant: 'success', ns: 'loyalty' }));
      } else {
        dispatch(
          showMessage({ message: 'loyalty.editPoints.successRemoveMessage', variant: 'success', ns: 'loyalty' }),
        );
      }
    } catch (_e) {
      dispatch(showMessage({ message: 'loyalty.editPoints.errorMessage', variant: 'error', ns: 'loyalty' }));
    } finally {
      dispatch(setCreditPointsLoading(false));
    }
  };

export const addClientSale = createAsyncThunk<
  LoyaltyCardInDTO,
  StoreSaleDTO,
  { state: RootState; dispatch: AppDispatch }
>('stores/addClientSaleThunk', async (sale, { dispatch, getState, rejectWithValue }) => {
  try {
    const loyaltyCard = await addSaleToClientApi(sale.storeId, sale.user.id, sale.amount);
    dispatch(showMessage({ message: 'loyalty.addSale.addSaleSuccess', variant: 'success', ns: 'loyalty' }));
    await refreshSelectedClient(dispatch, getState, sale.user.id);
    return loyaltyCard;
  } catch (error: any) {
    dispatch(showMessage({ message: 'loyalty.addSale.addSaleError', variant: 'error', ns: 'loyalty' }));
    return rejectWithValue(error.response.status);
  }
});

export const clientsHandleChangePage =
  (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => (dispatch: AppDispatch) => {
    const url = new URL(window.location.href);
    url.searchParams.set(ClientsFiltersUrlEnum.page, newPage.toString());
    window.history.pushState({}, '', url.toString());
    dispatch(setClientsPagination({ page: newPage }));
  };

export const clientsHandleChangeRowsPerPage =
  (event: React.ChangeEvent<HTMLInputElement>) => (dispatch: AppDispatch) => {
    dispatch(setClientsPagination({ page: PAGE, rowsPerPage: parseInt(event.target.value, 10) }));
    const url = new URL(window.location.href);
    url.searchParams.set(ClientsFiltersUrlEnum.rowsPerPage, event.target.value);
    url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
    window.history.pushState({}, '', url.toString());
  };

export const fetchColumnPreferencesThunk = () => async (dispatch: AppDispatch) => {
  try {
    const preferences = await getColumnPreferencesApi();
    dispatch(setColumnPreferences(preferences.columns));
  } catch (_error) {
    // A layout that cannot be read is not worth blocking the list for: the
    // table falls back to showing every column in its natural order.
  }
};

export const saveColumnPreferencesThunk =
  (columns: ColumnPreference[]) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const saved = await saveColumnPreferencesApi(
        keepUnlistedPreferences(columns, selectColumnPreferences(getState())),
      );
      dispatch(setColumnPreferences(saved.columns));
      dispatch(setColumnsDialogOpened(false));
    } catch (_error) {
      dispatch(showMessage({ message: i18n.t('pages.clients.columnsSaveFailed'), variant: 'error' }));
    }
  };

/**
 * Persist a column order the merchant produced by dragging a header.
 *
 * Applied before the request so the column follows the pointer, and put back
 * if the save fails: the table would otherwise show an order the server does
 * not have, which the next page load would silently undo.
 */
export const reorderColumnsThunk =
  (columns: ColumnPreference[]) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const previous = selectColumnPreferences(getState());
    const next = keepUnlistedPreferences(columns, previous);
    dispatch(setColumnPreferences(next));

    try {
      const saved = await saveColumnPreferencesApi(next);
      dispatch(setColumnPreferences(saved.columns));
    } catch (_error) {
      dispatch(setColumnPreferences(previous));
      dispatch(showMessage({ message: i18n.t('pages.clients.columnsSaveFailed'), variant: 'error' }));
    }
  };

export const clientsHandleSort =
  (key: ClientsSortKey, sort?: ClientsSortKey, sortOrder?: ClientsSortOrderEnum) => (dispatch: AppDispatch) => {
    if (
      key === ClientsSortTypeEnum.contact ||
      key === ClientsSortTypeEnum.clientType ||
      key === ClientsSortTypeEnum.acquiredFrom ||
      key === ClientsSortTypeEnum.lastName
    )
      return;
    const url = new URL(window.location.href);
    if (sort === key) {
      dispatch(
        setClientsSort({
          order:
            sortOrder === ClientsSortOrderEnum.ascending
              ? ClientsSortOrderEnum.descending
              : ClientsSortOrderEnum.ascending,
        }),
      );
      url.searchParams.set(
        ClientsSortsUrlEnum.order,
        sortOrder === ClientsSortOrderEnum.ascending ? ClientsSortOrderEnum.descending : ClientsSortOrderEnum.ascending,
      );
    } else {
      dispatch(setClientsSort({ type: key, order: ClientsSortOrderEnum.descending }));
      url.searchParams.set(ClientsSortsUrlEnum.sort, key);
      url.searchParams.set(ClientsSortsUrlEnum.order, ClientsSortOrderEnum.descending);
    }
    window.history.pushState({}, '', url.toString());
  };

export const onSelectStore = (storeId?: string) => (dispatch: AppDispatch) => {
  dispatch(setClientsFilters({ selectedStore: storeId }));
  dispatch(setClientsPagination({ page: PAGE }));
  const url = new URL(window.location.href);
  url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
  url.searchParams.delete(ClientsFiltersUrlEnum.store);
  if (storeId) {
    url.searchParams.set(ClientsFiltersUrlEnum.store, storeId);
  }
  window.history.pushState({}, '', url.toString());
};

export const onSelectAcquiredFrom = (newAcquiredFrom: AcquiredFromTypeEnum[]) => (dispatch: AppDispatch) => {
  dispatch(setClientsFilters({ acquiredFrom: newAcquiredFrom }));
  const url = new URL(window.location.href);
  url.searchParams.delete(ClientsFiltersUrlEnum.acquiredFrom);
  newAcquiredFrom.forEach(item => {
    url.searchParams.append(ClientsFiltersUrlEnum.acquiredFrom, item);
  });
  dispatch(setClientsPagination({ page: PAGE }));
  url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());
};

export const onSelectClientType = (newClientsType: ClientType[]) => (dispatch: AppDispatch) => {
  dispatch(setClientsFilters({ clientsType: newClientsType }));
  const url = new URL(window.location.href);
  url.searchParams.delete(ClientsFiltersUrlEnum.clientType);
  newClientsType.forEach(item => {
    url.searchParams.append(ClientsFiltersUrlEnum.clientType, item);
  });
  dispatch(setClientsPagination({ page: PAGE }));
  url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());
};

export const onSelectRecord = (record?: ClientRecord) => (dispatch: AppDispatch) => {
  dispatch(setClientsFilters({ record }));
  const url = new URL(window.location.href);
  url.searchParams.delete(ClientsFiltersUrlEnum.record);
  if (record) {
    url.searchParams.set(ClientsFiltersUrlEnum.record, record);
  }
  dispatch(setClientsPagination({ page: PAGE }));
  url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());
};

const ACTIVITY_URL_KEYS = [
  ClientsFiltersUrlEnum.activityFrom,
  ClientsFiltersUrlEnum.activityTo,
  ClientsFiltersUrlEnum.activityLast,
  ClientsFiltersUrlEnum.activityLastUnit,
];

export const onSelectActivityPeriod = (activityPeriod?: ActivityPeriod) => (dispatch: AppDispatch) => {
  dispatch(setClientsFilters({ activityPeriod }));
  const url = new URL(window.location.href);
  ACTIVITY_URL_KEYS.forEach(key => url.searchParams.delete(key));
  if (activityPeriod) {
    activityPeriodParams(activityPeriod).forEach(([key, value]) => url.searchParams.set(key, value));
  }
  dispatch(setClientsPagination({ page: PAGE }));
  url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());
};

/**
 * Read back a shared link; anything incomplete or malformed is dropped, as the API would refuse it.
 * Goes through the very check the control applies, so a link cannot carry what nobody could type.
 */
export const activityPeriodFromUrl = (url: URL): ActivityPeriod | undefined => {
  const from = url.searchParams.get(ClientsFiltersUrlEnum.activityFrom);
  const to = url.searchParams.get(ClientsFiltersUrlEnum.activityTo);
  const count = Number(url.searchParams.get(ClientsFiltersUrlEnum.activityLast));
  const unit = url.searchParams.get(ClientsFiltersUrlEnum.activityLastUnit);

  let candidate: ActivityPeriod | undefined;
  if (from && to) candidate = { mode: 'range', from, to };
  else if (isActivityPeriodUnit(unit)) candidate = { mode: 'last', count, unit };

  return candidate && periodFromDraft(draftFromPeriod(candidate), moment());
};

export const onSelectSegments = (newSegmentRefs: string[]) => (dispatch: AppDispatch) => {
  dispatch(setClientsFilters({ segmentRefs: newSegmentRefs }));
  const url = new URL(window.location.href);
  url.searchParams.delete(ClientsFiltersUrlEnum.segmentRefs);
  newSegmentRefs.forEach(ref => {
    url.searchParams.append(ClientsFiltersUrlEnum.segmentRefs, ref);
  });
  dispatch(setClientsPagination({ page: PAGE }));
  url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());
};

const CRM_URL_KEYS = [
  ClientsFiltersUrlEnum.crmFieldId,
  ClientsFiltersUrlEnum.crmSelected,
  ClientsFiltersUrlEnum.crmText,
  ClientsFiltersUrlEnum.crmBool,
  ClientsFiltersUrlEnum.crmMin,
  ClientsFiltersUrlEnum.crmMax,
  ClientsFiltersUrlEnum.crmMinDate,
  ClientsFiltersUrlEnum.crmMaxDate,
];

export const onSelectCrmFilter = (crmFilter?: CrmFilter) => (dispatch: AppDispatch) => {
  dispatch(setClientsFilters({ crmFilter }));
  const url = new URL(window.location.href);
  CRM_URL_KEYS.forEach(key => url.searchParams.delete(key));

  if (crmFilter) {
    crmFilterParams(crmFilter).forEach(([key, value]) => {
      if (key === ClientsFiltersUrlEnum.crmSelected) url.searchParams.append(key, value);
      else url.searchParams.set(key, value);
    });
  }

  dispatch(setClientsPagination({ page: PAGE }));
  url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());
};

/** Read back a shared link: a field with no value is dropped, as the API refuses it. */
export const crmFilterFromUrl = (url: URL): CrmFilter | undefined => {
  const fieldId = url.searchParams.get(ClientsFiltersUrlEnum.crmFieldId);
  if (!fieldId) return undefined;

  const selected = url.searchParams.getAll(ClientsFiltersUrlEnum.crmSelected);
  const text = url.searchParams.get(ClientsFiltersUrlEnum.crmText);
  const boolean = url.searchParams.get(ClientsFiltersUrlEnum.crmBool);
  const min = url.searchParams.get(ClientsFiltersUrlEnum.crmMin);
  const max = url.searchParams.get(ClientsFiltersUrlEnum.crmMax);
  const minDate = url.searchParams.get(ClientsFiltersUrlEnum.crmMinDate);
  const maxDate = url.searchParams.get(ClientsFiltersUrlEnum.crmMaxDate);

  const value: CrmFilterValue = {
    ...(selected.length ? { selected } : {}),
    ...(text ? { text } : {}),
    ...(boolean ? { boolean: boolean === 'true' } : {}),
    ...(min ? { min: Number(min) } : {}),
    ...(max ? { max: Number(max) } : {}),
    ...(minDate ? { minDate } : {}),
    ...(maxDate ? { maxDate } : {}),
  };

  return Object.keys(value).length ? { fieldId, value } : undefined;
};

export interface NewClientColumn {
  label: string;
  type: CrmFieldTypeEnum;
  options?: string[];
  isAskedToCustomer: boolean;
}

/**
 * Create a CRM field from the clients table and save the franchise's form.
 * Resolves to the saved field's id, or null when nothing was created.
 *
 * Reuses the CRM form's own add-then-save path rather than a dedicated
 * endpoint, so a column created here is the same object as one created from the
 * CRM page. A failed save removes the field again: leaving it would show a
 * column the server does not have, whose cells could never be filled.
 */
export const createCrmColumnThunk =
  ({ label, type, options, isAskedToCustomer }: NewClientColumn) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<string | null> => {
    // The save posts the whole form back, settings included, so a slice that
    // never received the franchise's form -- or is still fetching it -- would
    // reset crm.settings.points.
    if (getState().crm.hasError || getState().crm.isLoading) {
      dispatch(showMessage({ message: i18n.t('pages.crm.error.fetchCrmFieldError'), variant: 'error' }));
      return null;
    }

    const existingIds = new Set(crmFieldsSelector(getState()).map(field => field.id));
    dispatch(crmActions.addCrmField(type));
    const localId = getState().crm.selectedFieldId;
    if (!localId) return null;

    dispatch(
      crmActions.setCrmFieldById({
        id: String(localId),
        field: {
          label,
          isAskedToCustomer,
          validation: {
            type,
            isRequired: false,
            ...(options ? { options, isMultiSelect: false } : {}),
          },
        },
      }),
    );

    const result = await dispatch(saveCrmFieldsThunk());
    if (saveCrmFieldsThunk.rejected.match(result)) {
      dispatch(crmActions.deleteCrmField(localId));
      return null;
    }
    // The save swaps the local field for the server's copy. Its response also
    // carries archived fields, which may share the label, so match the new one.
    const saved = crmFieldsSelector(getState()).find(
      field => field.label === label && field.status === CrmFieldStatusEnum.ACTIVE && !existingIds.has(field.id),
    );
    return saved?.id ?? null;
  };

export const createClientColumnThunk = (column: NewClientColumn) => async (dispatch: AppDispatch) => {
  if (await dispatch(createCrmColumnThunk(column))) dispatch(setAddColumnDrawerOpened(false));
};

/**
 * Rename or remove a merchant-only column from the clients table, the only
 * place it shows. Removing archives it, as the CRM page does: the values stay.
 * A failed save puts the column back as it was.
 */
const changeClientColumnThunk =
  (fieldId: string, changes: Pick<Partial<ClientCrmField>, 'label' | 'status'>) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { crm } = getState();
    const previous = crm.entities[fieldId];
    if (crm.hasError || crm.isLoading || !previous) {
      dispatch(showMessage({ message: i18n.t('pages.crm.error.fetchCrmFieldError'), variant: 'error' }));
      return false;
    }

    dispatch(crmActions.setCrmFieldById({ id: fieldId, field: changes }));
    const result = await dispatch(saveCrmFieldsThunk());
    if (saveCrmFieldsThunk.rejected.match(result)) {
      dispatch(crmActions.setCrmFieldById({ id: fieldId, field: { label: previous.label, status: previous.status } }));
      return false;
    }
    return true;
  };

export const renameClientColumnThunk = (fieldId: string, label: string) => changeClientColumnThunk(fieldId, { label });

export const archiveClientColumnThunk = (fieldId: string) =>
  changeClientColumnThunk(fieldId, { status: CrmFieldStatusEnum.ARCHIVED });

export const resetSearchBar =
  (setTempSearchText: React.Dispatch<React.SetStateAction<string>>) => (dispatch: AppDispatch) => {
    dispatch(setClientsFilters({ searchText: '' }));
    setTempSearchText('');
    const url = new URL(window.location.href);
    url.searchParams.delete(ClientsFiltersUrlEnum.search);
    url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
    dispatch(setClientsPagination({ page: PAGE }));
    window.history.pushState({}, '', url.toString());
  };

export const onSearchChange = (tempSearchText: string) => (dispatch: AppDispatch) => {
  dispatch(setClientsFilters({ searchText: tempSearchText }));
  const url = new URL(window.location.href);
  url.searchParams.delete(ClientsFiltersUrlEnum.search);
  if (tempSearchText) {
    url.searchParams.append(ClientsFiltersUrlEnum.search, tempSearchText);
    url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
    dispatch(setClientsPagination({ page: PAGE }));
  }
  window.history.pushState({}, '', url.toString());
};

export const filtersFromUrl = (url: URL): ClientState['filters'] => ({
  searchText: url.searchParams.get(ClientsFiltersUrlEnum.search) || '',
  acquiredFrom: url.searchParams.getAll(ClientsFiltersUrlEnum.acquiredFrom) as AcquiredFromTypeEnum[],
  clientsType: url.searchParams.getAll(ClientsFiltersUrlEnum.clientType) as ClientType[],
  segmentRefs: url.searchParams.getAll(ClientsFiltersUrlEnum.segmentRefs),
  selectedStore: url.searchParams.get(ClientsFiltersUrlEnum.store) || '',
  crmFilter: crmFilterFromUrl(url),
  record: recordFromUrl(url),
  activityPeriod: activityPeriodFromUrl(url),
});

export const initClientsParams = () => (dispatch: AppDispatch) => {
  const url = new URL(window.location.href);

  if (!url.searchParams.get(ClientsFiltersUrlEnum.page)) {
    url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
  }

  if (!url.searchParams.get(ClientsFiltersUrlEnum.rowsPerPage)) {
    url.searchParams.set(ClientsFiltersUrlEnum.rowsPerPage, ROWS_PER_PAGE[0].toString());
  }

  if (!url.searchParams.get(ClientsSortsUrlEnum.sort)) {
    url.searchParams.set(ClientsSortsUrlEnum.sort, CLIENTS_SORT_DEFAULT);
  }

  if (!url.searchParams.get(ClientsSortsUrlEnum.order)) {
    url.searchParams.set(ClientsSortsUrlEnum.order, CLIENTS_SORT_ORDER_DEFAULT);
  }

  window.history.pushState({}, '', url.toString());

  dispatch(
    setClientsFiltersSortPagination({
      filters: filtersFromUrl(url),
      pagination: {
        page: parseInt(url.searchParams.get(ClientsFiltersUrlEnum.page) || PAGE.toString(), 10),
        rowsPerPage: parseInt(
          url.searchParams.get(ClientsFiltersUrlEnum.rowsPerPage) || ROWS_PER_PAGE[0].toString(),
          10,
        ),
      },
      sort: {
        type: (url.searchParams.get(ClientsSortsUrlEnum.sort) as ClientsSortKey) || CLIENTS_SORT_DEFAULT,
        order: (url.searchParams.get(ClientsSortsUrlEnum.order) as ClientsSortOrderEnum) || CLIENTS_SORT_ORDER_DEFAULT,
      },
    }),
  );
};

const recordFromUrl = (url: URL): ClientRecord | undefined => {
  const record = url.searchParams.get(ClientsFiltersUrlEnum.record);
  return isClientRecord(record) ? record : undefined;
};

export const resetClientsParams = () => (dispatch: AppDispatch) => {
  const url = new URL(window.location.href);

  Object.values(ClientsFiltersUrlEnum).forEach((key: string) => {
    url.searchParams.delete(key);
  });

  Object.values(ClientsSortsUrlEnum).forEach((key: string) => {
    url.searchParams.delete(key);
  });

  url.searchParams.set(ClientsFiltersUrlEnum.page, PAGE.toString());
  url.searchParams.set(ClientsFiltersUrlEnum.rowsPerPage, ROWS_PER_PAGE[0].toString());
  url.searchParams.set(ClientsSortsUrlEnum.sort, CLIENTS_SORT_DEFAULT);
  url.searchParams.set(ClientsSortsUrlEnum.order, CLIENTS_SORT_ORDER_DEFAULT);

  window.history.pushState({}, '', url.toString());

  dispatch(setClientsResetFiltersSortPagination());
};

export const addClientToStore =
  (storeId: StoreDTO['id'], clientId: ClientDetails['id']) => async (dispatch: any, getState: () => RootState) => {
    try {
      const franchise = selectUserFranchise(getState());
      dispatch(setLoading(true));
      const client = await addClientToStoreApi(storeId, clientId);
      await dispatch(getClientLoyaltyThunk(franchise.id, clientId, storeId));
      dispatch(setClientInformationSuccess(client));
      return client;
    } catch (_e) {
      dispatch(actions.setClientInformationFailed());
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const fetchUserCoupons =
  (userId: EntityId, page: number, size: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setClientCouponsLoading(true));
    try {
      const franchise = selectUserFranchise(getState());
      const coupons = await getUserCouponsApi(userId, page, size, franchise.id);
      dispatch(setClientCouponsSuccess(coupons));
    } catch (_e) {
      dispatch(setClientCouponsFailed());
    } finally {
      dispatch(setClientCouponsLoading(false));
    }
  };

export const redeemOffer =
  (clientId: EntityId, offerId: EntityId, storeId: EntityId) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const client = selectSelectedClient(getState());
    if (!client.info) return;

    dispatch(setRedeemOfferDialogLoading(true));
    try {
      await redeemOfferApi(clientId, storeId, offerId);
      dispatch(showMessage({ message: 'pages.clients.offersDialog.success', variant: 'success' }));
      dispatch(actions.setRedeemOfferDialogOpen(false));
      await refreshSelectedClient(dispatch, getState, clientId);
    } catch (error: unknown) {
      dispatch(showMessage({ message: redeemOfferErrorKey(error), variant: 'error' }));
    } finally {
      dispatch(setRedeemOfferDialogLoading(false));
    }
  };

export const redeemCoupon =
  (clientId: EntityId, franchiseId: EntityId, couponUserId: EntityId, storeId?: EntityId) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    // Checked against the store, not the rendered button: a second tap can land before the re-render that disables it.
    if (redeemCouponDialogSelector(getState()).isLoading) return;
    dispatch(setRedeemCouponDialogLoading(true));
    dispatch(setClientCouponsLoading(true));
    try {
      await redeemCouponApi(couponUserId);
      const { data } = await getUserCouponsApi(
        clientId,
        undefined,
        undefined,
        franchiseId as string,
        CouponStateEnum.ACTIVE,
        storeId,
      );
      dispatch(setClientSelectedCouponsSuccess(data));
      dispatch(showMessage({ message: 'pages.clients.couponsDialog.success', variant: 'success' }));
      dispatch(actions.setRedeemCouponDialogOpen(false));
    } catch (_e) {
      dispatch(showMessage({ message: 'pages.clients.couponsDialog.errors.redeem', variant: 'error' }));
    } finally {
      dispatch(setClientCouponsLoading(false));
      dispatch(setRedeemCouponDialogLoading(false));
    }
  };

export const deleteClientThunk = createAsyncThunk(
  'clients/deleteClient',
  async ({ franchiseId, clientId }: { franchiseId: EntityId; clientId: EntityId }, { dispatch }) => {
    try {
      await deleteClientApi(franchiseId, clientId);
      dispatch(showMessage({ message: 'pages.clients.deleteClient.success', variant: 'success' }));
      History.push(routesUrl.listClientsPage);
    } catch (_e) {
      dispatch(showMessage({ message: 'pages.clients.deleteClient.error', variant: 'error' }));
    }
  },
);
